import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {Button} from 'reactstrap';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './promotion.reducer';

export const Promotion = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const promotionList = useAppSelector(state => state.promotion.entities);
  const loading = useAppSelector(state => state.promotion.loading);
  const totalItems = useAppSelector(state => state.promotion.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="promotion-heading" data-cy="PromotionHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.promotion.home.title">Promotions</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" variant="outlined" color="info" onClick={handleSyncList} disabled={loading} size="small">
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="jhipsterSampleApplicationApp.promotion.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button component={Link} to="/promotion/new" variant="contained" color="primary" id="jh-create-entity" data-cy="entityCreateButton" size="small">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="jhipsterSampleApplicationApp.promotion.home.createLabel">Create new Promotion</Translate>
          </Button>
        </div>
      </h2>
      <div className="table-responsive">
        {promotionList && promotionList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.promotion.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.promotion.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('enabled')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.promotion.enabled">Enabled</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('enabled')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('discount')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.promotion.discount">Discount</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('discount')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('fromDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.promotion.fromDate">From Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('fromDate')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('toDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.promotion.toDate">To Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('toDate')} />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {promotionList.map((promotion, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/promotion/${promotion.id}`} variant="text" size="small">
                        {promotion.id}
                      </Button>
                    </TableCell>
                    <TableCell>{promotion.code}</TableCell>
                    <TableCell>{promotion.enabled ? 'true' : 'false'}</TableCell>
                    <TableCell>{promotion.discount}</TableCell>
                    <TableCell>{promotion.fromDate ? <TextFormat type="date" value={promotion.fromDate} format={APP_LOCAL_DATE_FORMAT} /> : null}</TableCell>
                    <TableCell>{promotion.toDate ? <TextFormat type="date" value={promotion.toDate} format={APP_LOCAL_DATE_FORMAT} /> : null}</TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button component={Link} to={`/promotion/${promotion.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`} color="primary" startIcon={<FontAwesomeIcon icon="pencil-alt" />}>
                        </Button>
                        <Button onClick={() => (window.location.href = `/promotion/${promotion.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)} color="error" startIcon={<FontAwesomeIcon icon="trash" />}>
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.promotion.home.notFound">No Promotions found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={promotionList && promotionList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Promotion;
